import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Check, X, Send, Eye, MousePointerClick, KeyRound, Hourglass } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phase F.5 Batch 14 — Employee Access-Request Workflow.
 *
 * Two-faced page:
 *  - Employees / portal users: see the module catalog + their request history; can submit new requests.
 *  - Admins: see all pending requests in their tenant + can approve/deny each.
 */
export default function AccessRequests() {
  const [catalog, setCatalog] = useState({ modules: [], is_admin: false });
  const [requests, setRequests] = useState([]);
  const [submitting, setSubmitting] = useState(null); // {module_key, label, access_level}
  const [statusFilter, setStatusFilter] = useState("all");
  const [decideTarget, setDecideTarget] = useState(null); // {req, action: 'approve'|'deny'}
  const [responseNote, setResponseNote] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/access-requests/catalog`),
        axios.get(`${API}/access-requests/`),
      ]);
      setCatalog(c.data || { modules: [], is_admin: false });
      setRequests(r.data?.requests || []);
    } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!submitting) return;
    try {
      await axios.post(`${API}/access-requests/`, {
        module_key: submitting.module_key,
        access_level: submitting.access_level,
        reason,
      });
      toast.success("Request submitted");
      setSubmitting(null); setReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
  };

  const decide = async () => {
    if (!decideTarget) return;
    try {
      const { req, action } = decideTarget;
      await axios.post(`${API}/access-requests/${req.id}/${action}`, {
        admin_response: responseNote,
      });
      toast.success(action === "approve" ? "Approved" : "Denied");
      setDecideTarget(null); setResponseNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const cancel = async (req) => {
    if (!window.confirm("Cancel this access request?")) return;
    try {
      await axios.post(`${API}/access-requests/${req.id}/cancel`);
      toast.success("Cancelled"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  const visibleRequests = statusFilter === "all"
    ? requests
    : requests.filter((r) => r.status === statusFilter);

  return (
    <div className="space-y-6 p-4" data-testid="access-requests-page">
      <div>
        <h1 className="text-2xl font-bold">Module Access Requests</h1>
        <p className="text-sm text-muted-foreground">
          {catalog.is_admin
            ? "Review and decide on access requests submitted by your employees."
            : "Request view-only or interact (edit) access to modules that aren't currently enabled for you."}
        </p>
      </div>

      <Tabs defaultValue={catalog.is_admin ? "review" : "catalog"}>
        <TabsList>
          {!catalog.is_admin && (
            <TabsTrigger value="catalog" data-testid="ar-tab-catalog">
              <KeyRound className="h-4 w-4 mr-1" /> Available modules
            </TabsTrigger>
          )}
          <TabsTrigger value="review" data-testid="ar-tab-review">
            <Hourglass className="h-4 w-4 mr-1" /> {catalog.is_admin ? "Pending review" : "My requests"}
          </TabsTrigger>
        </TabsList>

        {!catalog.is_admin && (
          <TabsContent value="catalog">
            <Card>
              <CardHeader>
                <CardTitle>Modules you can request</CardTitle>
                <CardDescription>
                  Pick a level: <strong>View</strong> (read-only) or <strong>Interact</strong> (edit).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="ar-module-list">
                  {catalog.modules.map((m) => (
                    <div key={m.key} className="p-3 border rounded-lg flex items-center justify-between gap-2 flex-wrap" data-testid={`ar-module-${m.key}`}>
                      <div className="min-w-0">
                        <p className="font-medium">{m.label}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" /> view: {m.has_view ? <Badge variant="default" className="ml-1">granted</Badge> : (m.pending_view ? <Badge variant="secondary" className="ml-1">pending</Badge> : <Badge variant="outline" className="ml-1">none</Badge>)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MousePointerClick className="h-3 w-3" /> interact: {m.has_interact ? <Badge variant="default" className="ml-1">granted</Badge> : (m.pending_interact ? <Badge variant="secondary" className="ml-1">pending</Badge> : <Badge variant="outline" className="ml-1">none</Badge>)}
                          </span>
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {!m.has_view && !m.pending_view && (
                          <Button size="sm" variant="outline" onClick={() => setSubmitting({ module_key: m.key, label: m.label, access_level: "view" })} data-testid={`ar-request-view-${m.key}`}>
                            <Eye className="h-3.5 w-3.5 mr-1" /> Request view
                          </Button>
                        )}
                        {!m.has_interact && !m.pending_interact && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setSubmitting({ module_key: m.key, label: m.label, access_level: "interact" })} data-testid={`ar-request-interact-${m.key}`}>
                            <MousePointerClick className="h-3.5 w-3.5 mr-1" /> Request interact
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="review">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <CardTitle>{catalog.is_admin ? "Pending and recent requests" : "My access requests"}</CardTitle>
                  <CardDescription>Filter by status to focus on what needs action.</CardDescription>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-44" data-testid="ar-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="denied">Denied</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {visibleRequests.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="ar-requests-empty">No requests in this view.</p>
              ) : (
                <div className="space-y-2" data-testid="ar-requests-list">
                  {visibleRequests.map((r) => {
                    const mod = catalog.modules.find((m) => m.key === r.module_key);
                    return (
                      <div key={r.id} className="p-3 border rounded-lg flex items-center justify-between gap-2 flex-wrap" data-testid={`ar-req-${r.id}`}>
                        <div className="min-w-0">
                          <p className="font-medium flex items-center gap-2 flex-wrap">
                            {(mod && mod.label) || r.module_key}
                            <Badge variant="outline">{r.access_level}</Badge>
                            <Badge variant={r.status === "approved" ? "default" : r.status === "denied" ? "destructive" : r.status === "cancelled" ? "secondary" : "outline"}>
                              {r.status}
                            </Badge>
                          </p>
                          {r.reason && <p className="text-xs text-muted-foreground mt-0.5">Reason: {r.reason}</p>}
                          {r.admin_response && <p className="text-xs text-muted-foreground italic">Admin: {r.admin_response}</p>}
                          <p className="text-[10px] text-muted-foreground mt-0.5">Submitted {new Date(r.created_at).toLocaleString()}</p>
                        </div>
                        <div className="flex gap-1">
                          {catalog.is_admin && r.status === "pending" && (
                            <>
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setDecideTarget({ req: r, action: "approve" })} data-testid={`ar-approve-${r.id}`}>
                                <Check className="h-3.5 w-3.5 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => setDecideTarget({ req: r, action: "deny" })} data-testid={`ar-deny-${r.id}`}>
                                <X className="h-3.5 w-3.5 mr-1" /> Deny
                              </Button>
                            </>
                          )}
                          {!catalog.is_admin && r.status === "pending" && (
                            <Button size="sm" variant="ghost" onClick={() => cancel(r)} data-testid={`ar-cancel-${r.id}`}>Cancel</Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Submit dialog */}
      <Dialog open={!!submitting} onOpenChange={(o) => !o && (setSubmitting(null), setReason(""))}>
        <DialogContent data-testid="ar-submit-dialog">
          <DialogHeader>
            <DialogTitle>Request access</DialogTitle>
            <DialogDescription>
              {submitting && (
                <>Requesting <strong>{submitting.access_level}</strong> access to <strong>{submitting.label}</strong>.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={4} placeholder="Optional: tell the admin why you need this." value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ar-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSubmitting(null); setReason(""); }}>Cancel</Button>
            <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ar-submit-btn"><Send className="h-4 w-4 mr-1" /> Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decide dialog */}
      <Dialog open={!!decideTarget} onOpenChange={(o) => !o && (setDecideTarget(null), setResponseNote(""))}>
        <DialogContent data-testid="ar-decide-dialog">
          <DialogHeader>
            <DialogTitle>{decideTarget?.action === "approve" ? "Approve request" : "Deny request"}</DialogTitle>
            <DialogDescription>
              {decideTarget && (
                <>{decideTarget.action === "approve" ? "Grant" : "Refuse"} {decideTarget.req.access_level} access to {decideTarget.req.module_key}.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={3} placeholder="Optional note for the employee." value={responseNote} onChange={(e) => setResponseNote(e.target.value)} data-testid="ar-response-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDecideTarget(null); setResponseNote(""); }}>Cancel</Button>
            <Button onClick={decide} className={decideTarget?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : ""} variant={decideTarget?.action === "deny" ? "destructive" : "default"} data-testid="ar-decide-confirm-btn">
              {decideTarget?.action === "approve" ? "Approve" : "Deny"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
